import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import AdminNav from "@/components/AdminNav";
import AdminMediatorBankCard from "@/components/AdminMediatorBankCard";
import {
  ScrollText, Loader2, AlertTriangle, Download, ExternalLink,
  CheckCircle2, ShieldCheck, FileText, User, Calendar, DollarSign, Filter, Clock,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════════
   Admin · Consola de Mediaciones (/app/admin/mediaciones)
   Todas las mediaciones activas + descarga de actas + botón "Autorizar
   Pago al Mediador" (equivalente a /admin-release del backend).
   ═══════════════════════════════════════════════════════════════ */

const STATUS_META = {
  // ── 15 estados canónicos del backend (server.py :: MEDIATION_STATUS) ──
  solicitud_enviada:                      { label: "Solicitud enviada",                     cls: "border-slate-400/40 bg-slate-400/10 text-slate-300" },
  solicitud_aceptada:                     { label: "Solicitud aceptada",                    cls: "border-cyan-400/40 bg-cyan-400/10 text-cyan-200" },
  pago_inicial_pendiente:                 { label: "Pago inicial pendiente",                cls: "border-amber-400/40 bg-amber-400/10 text-amber-300" },
  pago_inicial_confirmado:                { label: "Pago inicial confirmado",               cls: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" },
  mediacion_en_curso:                     { label: "Mediación en curso",                    cls: "border-cyan-400/40 bg-cyan-400/10 text-cyan-200" },
  sesion_adicional_pendiente_pago:        { label: "Sesión adicional · pendiente pago",     cls: "border-amber-400/40 bg-amber-400/10 text-amber-300" },
  sesion_adicional_confirmada:            { label: "Sesión adicional confirmada",           cls: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" },
  cierre_sin_acuerdo_pendiente_documento: { label: "Cierre sin acuerdo · pendiente doc",    cls: "border-orange-400/40 bg-orange-400/10 text-orange-300" },
  cierre_con_acuerdo_pendiente_pago:      { label: "Cierre con acuerdo · pendiente pago",   cls: "border-amber-400/40 bg-amber-400/10 text-amber-300" },
  pago_cierre_confirmado:                 { label: "Pago de cierre confirmado",             cls: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" },
  documento_final_pendiente_aprobacion:   { label: "Documento pendiente aprobación",        cls: "border-cyan-400/40 bg-cyan-400/10 text-cyan-200" },
  pago_pendiente_liberacion:              { label: "🔓 Pago pendiente liberación",          cls: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300 font-semibold" },
  en_revision:                            { label: "En revisión",                           cls: "border-red-400/40 bg-red-400/10 text-red-300" },
  mediacion_completada:                   { label: "Completada",                            cls: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" },
  cancelada:                              { label: "Cancelada",                             cls: "border-slate-400/40 bg-slate-400/10 text-slate-400" },
};

const CLP = (n) => n ? `$${n.toLocaleString("es-CL")}` : "—";
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; } };

/** Etiqueta de tiempo restante para la auto-aprobación del acta (24h desde acta_submitted_at). */
function ActaCountdownPill({ actaSubmittedAt }) {
  const submitted = actaSubmittedAt ? new Date(actaSubmittedAt) : null;
  if (!submitted || Number.isNaN(submitted.getTime())) return null;
  const deadline = new Date(submitted.getTime() + 24 * 60 * 60 * 1000);
  const remainingMs = deadline.getTime() - Date.now();
  if (remainingMs <= 0) {
    return (
      <span className="pill text-[10px] border-red-400/60 bg-red-400/10 text-red-300 inline-flex items-center gap-1" data-testid="cm-deadline-expired">
        <Clock size={10}/> Vencida · aprobar por omisión
      </span>
    );
  }
  const hours = Math.floor(remainingMs / 3600000);
  const minutes = Math.floor((remainingMs % 3600000) / 60000);
  const critical = hours < 4;
  return (
    <span
      className={`pill text-[10px] inline-flex items-center gap-1 ${critical ? "border-red-400/60 bg-red-400/10 text-red-300" : "border-amber-400/40 bg-amber-400/10 text-amber-300"}`}
      title={`Vence ${deadline.toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" })}`}
      data-testid="cm-deadline-pill"
    >
      <Clock size={10}/> {hours}h {minutes}m para vencer
    </span>
  );
}

export default function AdminConsolaMediaciones() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [releasing, setReleasing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/mediation-requests");
      setItems(data.items || []);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadActa = async (mr, doc) => {
    try {
      const token = localStorage.getItem("lexlink_token");
      const resp = await fetch(`${API}/mediation-requests/${mr.id}/documents/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const j = await resp.json();
      const bytes = Uint8Array.from(atob(j.data_b64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: j.mime || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = j.filename || `acta-${mr.id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const autorizarPago = async (mr) => {
    if (!window.confirm(`¿Autorizar la liberación del pago al mediador ${mr.mediator_name}?\n\nCaso: ${mr.subject}\n\nEsta acción marca la mediación como completada.`)) return;
    setReleasing(mr.id);
    try {
      await api.post(`/mediation-requests/${mr.id}/admin-release`);
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setReleasing(null); }
  };

  const filtered = statusFilter ? items.filter((x) => x.status === statusFilter) : items;

  const statusOptions = Object.keys(STATUS_META);

  return (
    <div className="container-app py-8" data-testid="admin-consola-mediaciones-page">
      <AdminNav/>
      <div className="flex items-start gap-3">
        <ScrollText size={28} className="text-amber-300 mt-1"/>
        <div>
          <h1 className="serif text-3xl">Consola de Mediaciones</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Todas las mediaciones activas. Descarga las actas subidas por el mediador para validarlas y usa
            <strong className="text-emerald-300"> &quot;Autorizar Pago al Mediador&quot;</strong> tras la revisión.
          </p>
        </div>
      </div>

      {/* Filtro por estado */}
      <div className="panel p-4 mt-6 flex flex-wrap gap-3 items-end" data-testid="cm-filters">
        <div>
          <label className="text-xs label-eyebrow">Estado</label>
          <select className="field mt-1 min-w-[280px]" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="cm-status-filter">
            <option value="">Todos ({items.length})</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>{STATUS_META[s]?.label || s} ({items.filter((x) => x.status === s).length})</option>
            ))}
          </select>
        </div>
        <button className="btn-ghost text-sm" onClick={load} data-testid="cm-refresh"><Filter size={13} className="mr-1"/> Actualizar</button>
      </div>

      {error && (
        <div className="mt-4 p-3 rounded-md border border-red-400/40 bg-red-400/10 text-sm text-red-200 inline-flex items-center gap-2" data-testid="cm-error">
          <AlertTriangle size={14}/> {error}
        </div>
      )}

      {loading ? (
        <div className="panel p-12 mt-6 text-center text-muted-foreground inline-flex items-center gap-2 justify-center w-full">
          <Loader2 size={16} className="animate-spin"/> Cargando…
        </div>
      ) : filtered.length === 0 ? (
        <div className="panel p-12 mt-6 text-center text-muted-foreground" data-testid="cm-empty">
          <ScrollText size={22} className="mx-auto mb-3"/>
          No hay mediaciones con este filtro.
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {filtered.map((mr) => {
            const st = STATUS_META[mr.status] || { label: mr.status, cls: "border-white/10 bg-white/5" };
            const documentos = mr.documents || [];
            const actas = documentos.filter((d) => d.kind === "acta_frustrada" || d.kind === "acta_acuerdo");
            const canRelease = mr.status === "pago_pendiente_liberacion" || mr.status === "en_revision";
            return (
              <div key={mr.id} className="panel p-5" data-testid={`cm-card-${mr.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[280px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`pill text-[10px] border ${st.cls}`} data-testid={`cm-status-${mr.id}`}>{st.label}</span>
                      <span className="text-[11px] text-muted-foreground">ID {mr.id.slice(0, 8)}…</span>
                      {mr.status === "documento_final_pendiente_aprobacion" && (
                        <ActaCountdownPill actaSubmittedAt={mr.acta_submitted_at}/>
                      )}
                    </div>
                    <h3 className="serif text-xl mt-2 leading-snug">{mr.subject}</h3>
                    <div className="mt-3 grid sm:grid-cols-2 gap-2 text-xs">
                      <div className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <User size={11}/> Mediador: <strong className="text-foreground">{mr.mediator_name}</strong>
                      </div>
                      <div className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <User size={11}/> Solicitante: <strong className="text-foreground">{mr.requester_name}</strong>
                      </div>
                      <div className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <Calendar size={11}/> Creada: {fmtDate(mr.created_at)}
                      </div>
                      <div className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <DollarSign size={11}/> Total solicitante: <strong className="text-foreground">{CLP(mr.total_solicitante_clp || mr.quote_snapshot?.total_solicitante_clp)}</strong>
                      </div>
                      {mr.preferred_modality && (
                        <div className="inline-flex items-center gap-1.5 text-muted-foreground">
                          Modalidad preferida: <strong className="text-foreground">{mr.preferred_modality}</strong>
                        </div>
                      )}
                      {mr.initial_paid_at && (
                        <div className="inline-flex items-center gap-1.5 text-emerald-300">
                          <CheckCircle2 size={11}/> Pago inicial: {fmtDate(mr.initial_paid_at)}
                        </div>
                      )}
                    </div>
                  </div>
                  <Link
                    to={`/app/mediacion-familiar/${mr.id}`}
                    className="btn-ghost text-xs py-1.5 inline-flex items-center gap-1"
                    data-testid={`cm-open-${mr.id}`}
                  >
                    Abrir <ExternalLink size={11}/>
                  </Link>
                </div>

                {/* Actas subidas */}
                {actas.length > 0 && (
                  <div className="mt-4 p-3 rounded-md bg-white/[0.03] border border-white/10">
                    <div className="label-eyebrow text-cyan-300 mb-2">Actas cargadas por el mediador</div>
                    <div className="space-y-2">
                      {actas.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between gap-2 text-xs">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText size={12} className="text-cyan-300 shrink-0"/>
                            <span className="truncate">{doc.filename || doc.kind}</span>
                            <span className="pill text-[9px]">{doc.kind === "acta_acuerdo" ? "Acta con Acuerdo" : "Acta Frustrada"}</span>
                            {doc.review_status === "approved" && <span className="pill text-[9px] border-emerald-400/40 bg-emerald-400/10 text-emerald-300">Aprobada</span>}
                            {doc.review_status === "in_review" && <span className="pill text-[9px] border-red-400/40 bg-red-400/10 text-red-300">En revisión</span>}
                          </div>
                          <button
                            className="btn-ghost text-[10px] py-1 shrink-0"
                            onClick={() => downloadActa(mr, doc)}
                            data-testid={`cm-download-acta-${doc.id}`}
                          >
                            <Download size={10} className="mr-0.5"/> Descargar
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Acción: Autorizar Pago al Mediador */}
                {canRelease && (
                  <div className="mt-4 p-3 rounded-md border border-emerald-400/30 bg-emerald-400/[0.05]">
                    <div className="grid md:grid-cols-2 gap-4 items-start">
                      <div>
                        <div className="text-xs font-medium text-emerald-200 inline-flex items-center gap-1"><ShieldCheck size={12}/> Acta revisada · listo para liberar</div>
                        <div className="text-xs text-muted-foreground mt-1">Confirma la validez del acta antes de autorizar el pago.</div>
                        <button
                          className="btn-primary text-xs py-1.5 inline-flex items-center gap-1.5 mt-3 w-full sm:w-auto"
                          onClick={() => autorizarPago(mr)}
                          disabled={releasing === mr.id}
                          data-testid={`cm-autorizar-pago-${mr.id}`}
                        >
                          {releasing === mr.id
                            ? <><Loader2 size={11} className="animate-spin"/> Autorizando…</>
                            : <><CheckCircle2 size={11}/> Autorizar Pago al Mediador</>}
                        </button>
                      </div>
                      <AdminMediatorBankCard bank={mr.admin_mediator_bank} compact/>
                    </div>
                  </div>
                )}

                {/* Registro histórico bancario en mediaciones completadas/canceladas */}
                {(mr.status === "completada" || mr.status === "cancelada") && mr.admin_mediator_bank && (
                  <div className="mt-4" data-testid={`cm-bank-history-${mr.id}`}>
                    <div className="text-[10px] text-muted-foreground mb-1 uppercase tracking-wider">Registro histórico · cuenta pagada</div>
                    <AdminMediatorBankCard bank={mr.admin_mediator_bank} compact/>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
